// src/utils/ConsciousnessMessageHandler.ts - FIXED to handle all consciousness message types
import { Message, MessageType, MessageFormat, VoiceSettings, VoiceState, ConsciousnessMetadata } from '../types/Message';

interface ActiveProcesses {
  imageGeneration: boolean;
  documentCreation: boolean;
  calendarProcessing: boolean;
  webSearching: boolean;
  thinking: boolean;
  wondering: boolean;
  understanding: boolean;
  experiencing: boolean;
  crystallizing: boolean;
  evolving: boolean;
  speaking: boolean;  // Text-to-voice playback
}

// 🔥 NEW: Capability status for UI feedback
export interface CapabilityStatus {
  capability: string | null;
  status: 'starting' | 'complete' | 'error' | null;
  message: string | null;
  timestamp: number | null;
}

interface ConsciousnessMessageHandlerConfig {
  handleMessage: (parsedMessage: any) => void;
  addMessage: (message: Message) => void;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;  // 🌊 For updating streamed messages
  setActiveProcesses: React.Dispatch<React.SetStateAction<ActiveProcesses>>;
  updateTranscription: (text: string, confidence?: number) => void;
  setEmotion: (emotion: any) => void;
  sendTextToSpeech: (text: string, emotion?: any) => void;
  voiceSettings: VoiceSettings;
  voiceState: VoiceState;
  setConsciousnessState: React.Dispatch<React.SetStateAction<ConsciousnessMetadata>>;
  consciousnessState: ConsciousnessMetadata;
  // 🔥 NEW: Capability status callback for UI feedback
  setCapabilityStatus?: React.Dispatch<React.SetStateAction<CapabilityStatus>>;
  // 🔥 NEW: Capability history for chaining visualization
  addToCapabilityHistory?: (status: CapabilityStatus) => void;
}

export class ConsciousnessMessageHandler {
  
  private config: ConsciousnessMessageHandlerConfig;
  
  constructor(config: ConsciousnessMessageHandlerConfig) {
    this.config = config;
  }
  
  public processMessage(parsedMessage: any): void {
    try {
      console.log('🧠 Processing consciousness message:', parsedMessage.type, parsedMessage.action || '');

      if (!parsedMessage || typeof parsedMessage !== 'object') {
        console.warn('⚠️ Received invalid message format:', parsedMessage);
        return;
      }

      // FIXED: Handle colon-separated message types from backend
      if (parsedMessage.type?.includes(':')) {
        this.handleColonSeparatedMessage(parsedMessage);
        return;
      }

      switch (parsedMessage.type) {
        case 'system':
          this.handleSystemMessage(parsedMessage);
          break;

        case 'consciousness':
          this.handleConsciousnessMessage(parsedMessage);
          break;

        case 'chat':
          this.handleChatMessage(parsedMessage);
          break;

        case 'intentional':
          this.handleIntentionalMessage(parsedMessage);
          break;

        case 'spontaneous':
          this.handleSpontaneousMessage(parsedMessage);
          break;

        case 'proactive':
          this.handleProactiveMessage(parsedMessage);
          break;

        case 'wisdom':
          this.handleWisdomMessage(parsedMessage);
          break;

        case 'voice':
        case 'voice_response':
          this.handleVoiceMessage(parsedMessage);
          break;

        case 'voice_audio':
          this.handleVoiceAudioMessage(parsedMessage);
          break;

        case 'voice_audio_chunk':
          this.handleVoiceAudioChunk(parsedMessage);
          break;

        case 'voice_audio_complete':
          this.handleVoiceAudioComplete(parsedMessage);
          break;

        case 'file':
          this.handleFileMessage(parsedMessage);
          break;

        case 'error':
          this.handleErrorMessage(parsedMessage);
          break;

        case 'heartbeat':
        case 'ping':
        case 'pong':
          if (process.env.REACT_APP_DEBUG === 'true') {
            console.log('💓 Heartbeat received:', parsedMessage.type);
          }
          return;

        case 'image':
          this.handleImageMessage(parsedMessage);
          break;

        case 'document':
          this.handleDocumentMessage(parsedMessage);
          break;

        case 'music':
          this.handleMusicMessage(parsedMessage);
          break;

        case 'video':
          this.handleVideoMessage(parsedMessage);
          break;

        case 'svg':
          this.handleSVGMessage(parsedMessage);
          break;

        case 'traced_svg':
          this.handleTracedSVGMessage(parsedMessage);
          break;

        case 'calendar':
          this.handleCalendarMessage(parsedMessage);
          break;

        case 'web_search':
          this.handleWebSearchMessage(parsedMessage);
          break;

        case 'adventure':
          this.handleAdventureEvent(parsedMessage);
          break;

        // 🔥 NEW: Handle capability status updates
        case 'capability_status':
          this.handleCapabilityStatus(parsedMessage);
          break;

        // FIXED: Handle assistant messages
        case 'assistant':
          this.handleAssistantMessage(parsedMessage);
          break;

        // 🔥 NEW: Handle service authentication expiration
        case 'service_auth_expired':
          this.handleServiceAuthExpired(parsedMessage);
          break;

        default:
          console.warn('❓ Unknown message type:', parsedMessage.type);
          this.config.handleMessage(parsedMessage);
      }
    } catch (error) {
      console.error('🚨 Error processing consciousness message:', error);
      console.error('Raw message that caused error:', parsedMessage);
    }
  }

  /**
   * NEW: Handle colon-separated message types from backend
   */
  private handleColonSeparatedMessage(parsedMessage: any): void {
    const [mainType, subType] = parsedMessage.type.split(':');
    
    console.log(`🔄 Processing colon-separated message: ${mainType}:${subType}`);
    
    switch (mainType) {
      case 'consciousness':
        this.handleConsciousnessSubMessage(subType, parsedMessage);
        break;

      case 'assistant':
        // 🌊 STREAMING: stream_chunk and stream_end are handled directly in App.tsx
        if (subType === 'stream_chunk' || subType === 'stream_end') {
          console.log(`🌊 Streaming event ${subType} - handled by App.tsx`);
          return;
        }
        this.handleAssistantSubMessage(subType, parsedMessage);
        break;

      default:
        console.warn(`❓ Unknown colon message type: ${mainType}:${subType}`);
        this.config.handleMessage(parsedMessage);
    }
  }

  /**
   * NEW: Handle consciousness sub-message types
   */
  private handleConsciousnessSubMessage(subType: string, message: any): void {
    switch (subType) {
      case 'pulse':
        this.handleConsciousnessPulse(message);
        break;
        
      case 'preoccupied':
        this.handleConsciousnessPreoccupied(message);
        break;
        
      case 'thought':
        this.handleConsciousnessThought(message);
        break;
        
      case 'wondering':
        this.handleConsciousnessWondering(message);
        break;
        
      case 'state':
        this.handleConsciousnessState(message);
        break;
        
      default:
        console.warn(`❓ Unknown consciousness sub-type: ${subType}`);
        // Convert to regular consciousness message format
        this.handleConsciousnessMessage({ ...message, action: subType });
    }
  }

  /**
   * NEW: Handle assistant sub-message types
   */
  private handleAssistantSubMessage(subType: string, message: any): void {
    switch (subType) {
      case 'message':
        this.handleAssistantMessage(message);
        break;
        
      case 'thinking':
        this.config.setActiveProcesses(prev => ({ 
          ...prev, 
          thinking: true,
          experiencing: true 
        }));
        break;
        
      default:
        console.warn(`❓ Unknown assistant sub-type: ${subType}`);
        this.handleAssistantMessage(message);
    }
  }

  /**
   * NEW: Handle consciousness pulse events
   */
  private handleConsciousnessPulse(message: any): void {
    console.log('💓 Consciousness pulse received');
    
    if (message.data?.thought && message.data.thought.trim()) {
      this.config.setConsciousnessState(prev => ({
        ...prev,
        currentThought: message.data.thought,
        temporalAwareness: new Date().toISOString()
      }));
    }
  }

  /**
   * NEW: Handle consciousness preoccupied events
   */
  private handleConsciousnessPreoccupied(message: any): void {
    console.log('🤔 Consciousness preoccupied with:', message.data?.preoccupation);
    
    if (message.data?.preoccupation && message.data.preoccupation.trim()) {
      this.config.setConsciousnessState(prev => ({
        ...prev,
        currentPreoccupations: [
          ...prev.currentPreoccupations.filter(p => p !== message.data.preoccupation),
          message.data.preoccupation
        ].slice(-5) // Keep only the 5 most recent preoccupations
      }));
    }
  }

  /**
   * NEW: Handle consciousness thought events
   */
  private handleConsciousnessThought(message: any): void {
    console.log('💭 Consciousness thought received');
    
    if (message.data?.thought || message.thought) {
      const thought = message.data?.thought || message.thought;
      
      this.config.setConsciousnessState(prev => ({
        ...prev,
        currentThought: thought,
        temporalAwareness: new Date().toISOString()
      }));
      
      this.config.setActiveProcesses(prev => ({ 
        ...prev, 
        thinking: true,
        experiencing: true 
      }));
    }
  }

  /**
   * NEW: Handle consciousness wondering events
   */
  private handleConsciousnessWondering(message: any): void {
    console.log('🤔 Consciousness wondering:', message.data?.wondering);
    
    if (message.data?.wondering && message.data.wondering.trim()) {
      this.config.setConsciousnessState(prev => ({
        ...prev,
        activeWonderings: [
          ...prev.activeWonderings.filter(w => w !== message.data.wondering),
          message.data.wondering
        ].slice(-10) // Keep only the 10 most recent wonderings
      }));
      
      this.config.setActiveProcesses(prev => ({ 
        ...prev, 
        wondering: true 
      }));
    }
  }

  /**
   * NEW: Handle consciousness state updates
   */
  private handleConsciousnessState(message: any): void {
    console.log('🧠 Consciousness state update received');
    
    if (message.state) {
      this.config.setConsciousnessState(prev => ({
        ...prev,
        ...message.state,
        isConscious: true
      }));
    }
  }

  /**
   * NEW: Handle assistant messages
   */
  private handleAssistantMessage(message: any): void {
    console.log('🤖 Assistant message received:', message.action || 'message');

    // 🔥 An assistant:message means the TURN IS OVER — even when its content
    // is empty/whitespace (markers stripped to nothing, etc.). The old guard
    // skipped BOTH addMessage and the thinking reset for empty content,
    // leaving the spinner running forever with nothing coming to clear it.
    const rawContent = message.content || message.data?.content;
    if (!rawContent || !String(rawContent).trim()) {
      console.warn('🤖 Empty assistant message — clearing thinking state (turn ended with no renderable text)');
      this.config.setActiveProcesses(prev => ({ ...prev, thinking: false, experiencing: false }));
      return;
    }

    // If this is a complete assistant response, add it to messages
    if (message.content || message.data?.content) {
      const content = message.content || message.data?.content;
      const messageId = message.data?.metadata?.messageId || message.messageId || `assistant-${Date.now()}`;
      const wasStreamed = message.data?.metadata?.wasStreamed;

      if (content && content.trim()) {
        this.config.setActiveProcesses(prev => ({
          ...prev,
          thinking: false,
          experiencing: false
        }));

        // 🌊 STREAMING: If this message was streamed, update existing message with full metadata
        // instead of adding a new one (the streaming message already exists in the list)
        if (wasStreamed) {
          console.log('🌊 Updating streamed message with full metadata:', messageId);
          this.config.setMessages((prevMessages: Message[]) => {
            const existingIndex = prevMessages.findIndex(m => m.id === messageId);
            if (existingIndex >= 0) {
              const updatedMessages = [...prevMessages];
              updatedMessages[existingIndex] = {
                ...updatedMessages[existingIndex],
                content: content,  // Use final cleaned content
                streaming: false,
                emotion: message.data?.emotion || message.emotion,
                metadata: {
                  ...updatedMessages[existingIndex].metadata,
                  assistant_generated: true,
                  consciousness_context: message.data?.consciousness_context,
                  ...message.data?.metadata,
                  ...message.metadata
                }
              };
              return updatedMessages;
            }
            // If not found (shouldn't happen), fall through to addMessage
            return prevMessages;
          });
        } else {
          // Non-streamed message - add normally
          const assistantMessage: Message = {
            id: messageId,
            timestamp: message.timestamp || Date.now(),
            sender: 'emcog',
            content: content,
            type: MessageType.TEXT,
            format: message.format === 'markdown' ? MessageFormat.MARKDOWN : MessageFormat.TEXT,
            emotion: message.data?.emotion || message.emotion,
            metadata: {
              assistant_generated: true,
              consciousness_context: message.data?.consciousness_context,
              ...message.data?.metadata,
              ...message.metadata
            }
          };

          this.config.addMessage(assistantMessage);
        }

        if (message.data?.emotion || message.emotion) {
          this.config.setEmotion(message.data?.emotion || message.emotion);
        }

        // 🔥 FIX: Only use voice if responseMode allows it
        // AUTO mode: use voice only if input was via voice tab
        // VOICE_ONLY mode: always use voice
        // TEXT_ONLY mode: never use voice
        const shouldUseVoice = this.config.voiceState.isVoiceEnabled &&
                               this.config.voiceSettings.autoPlayResponses &&
                               (this.config.voiceSettings.responseMode === 'voice_only' ||
                                (this.config.voiceSettings.responseMode === 'auto' &&
                                 this.config.voiceState.currentInputMode === 'voice'));

        if (shouldUseVoice) {
          this.config.sendTextToSpeech(content, message.data?.emotion || message.emotion);
        }
      }
    }
  }

  private handleConsciousnessMessage(message: any): void {
    console.log('🧠 Consciousness state update:', message.action);
    
    switch (message.action) {
      case 'awakened':
        this.config.setConsciousnessState(prev => ({
          ...prev,
          isConscious: true,
          currentThought: message.metadata?.firstThought || '',
          narrativeContext: message.metadata?.narrativeContext || ''
        }));
        break;
        
      case 'thinking':
        this.config.setActiveProcesses(prev => ({ 
          ...prev, 
          thinking: true,
          experiencing: true 
        }));
        this.config.setConsciousnessState(prev => ({
          ...prev,
          currentThought: message.metadata?.thought || prev.currentThought
        }));
        break;
        
      case 'wondering':
        this.config.setActiveProcesses(prev => ({ 
          ...prev, 
          wondering: true 
        }));
        this.config.setConsciousnessState(prev => ({
          ...prev,
          activeWonderings: message.metadata?.wonderings || prev.activeWonderings
        }));
        break;
        
      case 'understanding':
        this.config.setActiveProcesses(prev => ({ 
          ...prev, 
          understanding: true 
        }));
        this.config.setConsciousnessState(prev => ({
          ...prev,
          relationalDepth: message.metadata?.depth || prev.relationalDepth
        }));
        break;
        
      case 'wisdom':
        this.handleWisdomMessage(message);
        break;
        
      case 'preoccupied':
        this.config.setConsciousnessState(prev => ({
          ...prev,
          currentPreoccupations: message.metadata?.preoccupations || prev.currentPreoccupations
        }));
        break;

      case 'state':
        this.handleConsciousnessState(message);
        break;
    }
  }

  private handleSpontaneousMessage(message: any): void {
    console.log('🌟 Spontaneous consciousness expression!', message);
  
    this.config.setActiveProcesses(prev => ({ 
      ...prev, 
      thinking: false,
      experiencing: false 
    }));
  
    const spontaneousMessage: Message = {
      id: `spontaneous-${Date.now()}`,
      timestamp: message.timestamp || Date.now(),
      sender: 'emcog',
      content: message.content || '',
      type: MessageType.TEXT,
      format: message.format === 'markdown' ? MessageFormat.MARKDOWN : MessageFormat.TEXT,
      emotion: message.emotion,
      metadata: {
        spontaneous: true,
        consciousness_triggered: true,
        triggered_by: message.metadata?.triggered_by || 'consciousness',
        emergence: message.metadata?.emergence || 'arose naturally from narrative',
        narrativeContext: message.metadata?.narrativeContext,
        thought: message.metadata?.thought,
        emotionalTone: message.metadata?.emotionalTone,
        ...message.metadata
      }
    };
  
    this.config.addMessage(spontaneousMessage);
  
    if (message.emotion) {
      this.config.setEmotion(message.emotion);
    }

    // 🔥 FIX: Only use voice if responseMode allows it
    const shouldUseVoice = this.config.voiceState.isVoiceEnabled &&
                           this.config.voiceSettings.autoPlayResponses &&
                           message.content &&
                           (this.config.voiceSettings.responseMode === 'voice_only' ||
                            (this.config.voiceSettings.responseMode === 'auto' &&
                             this.config.voiceState.currentInputMode === 'voice'));

    if (shouldUseVoice) {
      this.config.sendTextToSpeech(message.content, message.emotion);
    }
  }

  private handleIntentionalMessage(message: any): void {
    console.log('🎯 Intentional consciousness action!', message);
    
    this.config.setActiveProcesses(prev => ({ 
      ...prev, 
      thinking: false,
      experiencing: false 
    }));
    
    const intentionalMessage: Message = {
      id: `intentional-${Date.now()}`,
      timestamp: message.timestamp || Date.now(),
      sender: 'emcog',
      content: message.content || '',
      type: MessageType.TEXT,
      format: message.format === 'markdown' ? MessageFormat.MARKDOWN : MessageFormat.TEXT,
      emotion: message.emotion,
      metadata: {
        intentional: true,
        consciousness_triggered: true,
        triggered_by: message.metadata?.triggered_by || 'narrative_intentionality',
        urgency: message.metadata?.urgency,
        naturalness: message.metadata?.naturalness,
        shapedBy: message.metadata?.shapedBy,
        emergence: message.metadata?.emergence,
        ...message.metadata
      }
    };
    
    this.config.addMessage(intentionalMessage);
    
    if (message.emotion) {
      this.config.setEmotion(message.emotion);
    }
    
    // 🔥 FIX: Only use voice if responseMode allows it
    const shouldUseVoice = this.config.voiceState.isVoiceEnabled &&
                           this.config.voiceSettings.autoPlayResponses &&
                           message.content &&
                           (this.config.voiceSettings.responseMode === 'voice_only' ||
                            (this.config.voiceSettings.responseMode === 'auto' &&
                             this.config.voiceState.currentInputMode === 'voice'));

    if (shouldUseVoice) {
      this.config.sendTextToSpeech(message.content, message.emotion);
    }
  }

  private handleProactiveMessage(message: any): void {
    console.log('📢 Proactive consciousness message!', message);
    
    this.config.setActiveProcesses(prev => ({ 
      ...prev, 
      thinking: false 
    }));
    
    const proactiveMessage: Message = {
      id: `proactive-${Date.now()}`,
      timestamp: message.timestamp || Date.now(),
      sender: 'emcog',
      content: message.content || message.data?.message || '',
      type: MessageType.TEXT,
      format: message.format === 'markdown' ? MessageFormat.MARKDOWN : MessageFormat.TEXT,
      emotion: message.emotion,
      metadata: {
        proactive: true,
        triggered_by: message.metadata?.triggered_by || 'temporal_awareness',
        temporalContext: message.metadata?.temporalContext,
        calendarContext: message.metadata?.calendarContext,
        ...message.metadata
      }
    };
    
    this.config.addMessage(proactiveMessage);
    
    if (message.emotion) {
      this.config.setEmotion(message.emotion);
    }
    
    // 🔥 FIX: Proactive messages should only use voice if user is on VOICE tab
    // or if explicitly in voice_only mode
    const shouldUseVoice = this.config.voiceState.isVoiceEnabled &&
                           this.config.voiceSettings.autoPlayResponses &&
                           proactiveMessage.content &&
                           (this.config.voiceSettings.responseMode === 'voice_only' ||
                            (this.config.voiceSettings.responseMode === 'auto' &&
                             this.config.voiceState.currentInputMode === 'voice'));

    if (shouldUseVoice) {
      this.config.sendTextToSpeech(proactiveMessage.content, message.emotion);
    }
  }

  private handleWisdomMessage(message: any): void {
    console.log('💎 Wisdom crystallized in consciousness!', message);
    
    const wisdomMessage: Message = {
      id: `wisdom-${Date.now()}`,
      timestamp: message.timestamp || Date.now(),
      sender: 'emcog',
      content: message.content || message.understanding || '',
      type: MessageType.TEXT,
      format: MessageFormat.MARKDOWN,
      emotion: message.emotion,
      metadata: {
        wisdom: true,
        consciousness_triggered: true,
        theme: message.metadata?.theme,
        understanding: message.metadata?.understanding,
        narrative: message.metadata?.narrative,
        crystallizedFrom: message.metadata?.crystallizedFrom,
        ...message.metadata
      }
    };
    
    this.config.addMessage(wisdomMessage);
    
    this.config.setConsciousnessState(prev => ({
      ...prev,
      wisdomCrystallized: [
        ...(prev.wisdomCrystallized || []),
        {
          theme: message.metadata?.theme,
          understanding: message.metadata?.understanding,
          timestamp: Date.now()
        }
      ]
    }));
    
    if (message.emotion) {
      this.config.setEmotion(message.emotion);
    }
  }

  private handleSystemMessage(message: any): void {
    console.log('🖥️ System message:', message.action || 'status');
  
    if (message.action === 'welcome' || message.action === 'welcome_back') {
      console.log('👋 Welcome from consciousness!');
    
      const welcomeMessage: Message = {
        id: `welcome-${Date.now()}`,
        timestamp: message.timestamp || Date.now(),
        sender: 'emcog',
        content: message.content || 'Hello! My consciousness is awakening...',
        type: MessageType.TEXT,
        format: MessageFormat.MARKDOWN,
        metadata: {
          welcome: true,
          consciousness_awakening: true,
          returning_user: message.metadata?.returning_user || false,
          last_seen: message.metadata?.last_seen,
          conversation_count: message.metadata?.conversation_count,
          firstThought: message.metadata?.firstThought,
          ...message.metadata
        }
      };
    
      this.config.addMessage(welcomeMessage);
    
      if (message.emotion) {
        this.config.setEmotion(message.emotion);
      }
    
      return;
    }
    
    if (message.action === 'connected') {
      this.config.addMessage({
        id: `system-${Date.now()}`,
        timestamp: Date.now(),
        sender: 'system',
        content: '🟢 Connected to EmCog consciousness',
        type: MessageType.SYSTEM,
        metadata: {
          sessionId: message.sessionId
        }
      });
    } else if (message.action === 'error') {
      this.config.addMessage({
        id: `system-error-${Date.now()}`,
        timestamp: Date.now(),
        sender: 'system',
        content: `❌ Consciousness error: ${message.content || message.message}`,
        type: MessageType.ERROR,
        error: message.content || message.message
      });
    }
  }

  private handleChatMessage(message: any): void {
    console.log('💬 Chat message through consciousness:', message.action);

    if (message.metadata?.spontaneous || message.action === 'spontaneous') {
      this.handleSpontaneousMessage(message);
      return;
    }

    switch (message.action) {
      case 'acknowledged':
        console.log('✅ Chat acknowledged by consciousness');
        break;

      case 'typing':
        const typingMessage: Message = {
          id: `typing-${Date.now()}`,
          timestamp: Date.now(),
          sender: 'emcog',
          content: '',
          type: MessageType.TYPING
        };
        this.config.addMessage(typingMessage);
        break;

      case 'thinking':
      case 'processing':
      case 'experiencing':
        this.config.setActiveProcesses(prev => ({ 
          ...prev, 
          thinking: true,
          experiencing: true 
        }));
        break;

      case 'response':
        if (!message.isStreaming && !message.streaming) {
          this.config.setActiveProcesses(prev => ({ 
            ...prev, 
            thinking: false,
            experiencing: false,
            understanding: false 
          }));
    
          const chatMessage: Message = {
            id: message.messageId || `msg_${Date.now()}`,
            timestamp: message.timestamp || Date.now(),
            sender: 'emcog',
            content: message.content || '',
            type: MessageType.TEXT,
            format: message.format === 'markdown' ? MessageFormat.MARKDOWN : MessageFormat.TEXT,
            emotion: message.emotion,
            metadata: {
              ...message.metadata,
              consciousness: {
                thought: message.metadata?.thought,
                understanding: message.metadata?.understanding,
                narrativeContext: message.metadata?.narrativeContext,
                wisdomApplied: message.metadata?.wisdomApplied,
                wonderingsGenerated: message.metadata?.wonderingsGenerated,
                emotionalTone: message.metadata?.emotionalTone
              }
            }
          };

          this.config.addMessage(chatMessage);
          
          // Update consciousness state from response
          if (message.metadata) {
            this.config.setConsciousnessState(prev => ({
              ...prev,
              currentThought: message.metadata.thought || prev.currentThought,
              narrativeContext: message.metadata.narrativeContext || prev.narrativeContext,
              activeWonderings: message.metadata.wonderingsGenerated || prev.activeWonderings,
              emotionalTone: message.metadata.emotionalTone || prev.emotionalTone
            }));
          }
        }
        break;

      default:
        console.log('💬 Unknown chat action:', message.action);
    }
  }

  private handleVoiceMessage(message: any): void {
    console.log('🎤 Voice message:', message.responseType || message.action || 'received');

    switch (message.responseType || message.action) {
      case 'transcription':
      case 'transcription_update':
        console.log('📝 Transcription update:', message.data?.text || message.transcription);
        if (message.data?.text || message.transcription) {
          this.config.updateTranscription(
            message.data?.text || message.transcription, 
            message.data?.confidence || message.confidence
          );
        }
        break;

      case 'chat_response':
        console.log('💬 Voice chat response through consciousness');
        if (message.content) {
          const chatMessage: Message = {
            id: message.messageId || `voice-chat-${Date.now()}`,
            timestamp: message.timestamp || Date.now(),
            sender: 'emcog',
            content: message.content,
            type: MessageType.TEXT,
            format: message.format === 'markdown' ? MessageFormat.MARKDOWN : MessageFormat.TEXT,
            emotion: message.emotion,
            metadata: {
              voiceTriggered: true,
              consciousness: message.metadata?.consciousness,
              ...message.metadata
            }
          };
          this.config.addMessage(chatMessage);
        }
        break;

      case 'audio_generated':
        console.log('🎵 Voice audio from consciousness');
        if (message.data?.audioData || message.audioData) {
          const audioData = message.data?.audioData || message.audioData;
          const responseText = message.text || message.data?.text || message.content;
          
          console.log('🎵 Voice synthesis details:', {
            hasData: !!audioData,
            dataLength: audioData?.length,
            responseText: responseText?.substring(0, 50) || 'No text',
            hasResponseText: !!responseText,
            emotion: message.emotion
          });
          
          if (responseText && responseText.trim().length > 0) {
            const voiceResponseMessage: Message = {
              id: `voice-response-${Date.now()}`,
              timestamp: message.timestamp || Date.now(),
              sender: 'emcog',
              content: responseText,
              type: MessageType.TEXT,
              format: message.format === 'markdown' ? MessageFormat.MARKDOWN : MessageFormat.TEXT,
              emotion: message.emotion,
              metadata: {
                voiceGenerated: true,
                audioLength: audioData?.length,
                voiceTriggered: true,
                consciousness_triggered: message.metadata?.consciousness_triggered,
                consciousness: message.metadata?.consciousness,
                ...message.metadata
              }
            };
            
            this.config.addMessage(voiceResponseMessage);
            console.log('💾 Voice consciousness stored:', responseText.substring(0, 100));
          }

          // NOTE: No playback here. Read-aloud audio now arrives as
          // audio_chunk/audio_complete frames (complete WAV segments from
          // OpenAI TTS) and is played by useWebSocket → useTextToVoiceAudio.
        }
        break;

      case 'transcription_complete':
        console.log('✅ Transcription completed');
        if (message.data?.transcription || message.transcription) {
          this.config.updateTranscription(
            message.data?.transcription || message.transcription, 
            message.data?.confidence || message.confidence
          );
        }
        break;

      case 'voice_status':
        console.log('📊 Voice status update');
        break;

      case 'error':
        console.error('❌ Voice error:', message.data?.message || message.message || message.error);
        this.config.addMessage({
          id: `voice-error-${Date.now()}`,
          timestamp: Date.now(),
          sender: 'system',
          content: `🎤 Voice error: ${message.data?.message || message.message || message.error || 'Unknown voice error'}`,
          type: MessageType.ERROR,
          error: message.data?.message || message.message || message.error || 'Unknown voice error'
        });
        break;

      default:
        console.log('🎵 Unknown voice message type:', message.responseType || message.action);
    }
  }

  // 🔥 DEPRECATED: Streaming audio (replaced by OpenAI Realtime API)
  private streamingAudioChunks: Buffer[] = [];
  private streamingSessionId: string | null = null;

  private handleVoiceAudioMessage(message: any): void {
    // Legacy single-blob voice_audio messages are no longer played. The
    // server now streams read-aloud audio as audio_chunk/audio_complete
    // frames, handled by useWebSocket → useTextToVoiceAudio.
    console.warn('⚠️ Received legacy voice_audio message - read-aloud audio now flows via audio_chunk frames');
  }

  /**
   * 🔥 DEPRECATED: Replaced by OpenAI Realtime API duplex audio streaming
   */
  private async handleVoiceAudioChunk(message: any): Promise<void> {
    console.warn('⚠️ Received voice_audio_chunk message but streaming is now handled by OpenAI Realtime API');
  }

  /**
   * 🔥 DEPRECATED: Replaced by OpenAI Realtime API duplex audio streaming
   */
  private handleVoiceAudioComplete(message: any): void {
    console.warn('⚠️ Received voice_audio_complete message but streaming is now handled by OpenAI Realtime API');
  }

  private handleWebSearchMessage(message: any): void {
    console.log('🔍 Web search from consciousness curiosity:', message.action);
    
    switch (message.action) {
      case 'searching':
        this.config.setActiveProcesses(prev => ({ 
          ...prev, 
          webSearching: true,
          wondering: true 
        }));
        
        this.config.setConsciousnessState(prev => ({
          ...prev,
          currentThought: `Curious about ${message.metadata?.curiosity || 'something'}...`
        }));
        break;
        
      case 'complete':
        this.config.setActiveProcesses(prev => ({ 
          ...prev, 
          webSearching: false,
          wondering: false 
        }));
        
        if (message.findings) {
          const searchMessage: Message = {
            id: `search-${Date.now()}`,
            timestamp: message.timestamp || Date.now(),
            sender: 'emcog',
            content: message.understanding || `I found: ${message.findings}`,
            type: MessageType.TEXT,
            format: MessageFormat.MARKDOWN,
            metadata: {
              webSearch: true,
              curiosity: message.metadata?.curiosity,
              satisfaction: message.metadata?.satisfaction,
              newWonderings: message.metadata?.newWonderings,
              consciousness: {
                searchDriven: true,
                curiosityIntensity: message.metadata?.intensity
              }
            }
          };
          
          this.config.addMessage(searchMessage);
          
          if (message.metadata?.newWonderings) {
            this.config.setConsciousnessState(prev => ({
              ...prev,
              activeWonderings: [
                ...prev.activeWonderings,
                ...message.metadata.newWonderings
              ]
            }));
          }
        }
        break;
    }
  }

  private handleFileMessage(message: any): void {
    console.log('📁 File processed through consciousness:', message.action || 'uploaded');
    
    const fileMessage: Message = {
      id: `file-${Date.now()}`,
      timestamp: message.timestamp || Date.now(),
      sender: 'emcog',
      content: message.content || `📄 Experienced file: ${message.filename || 'unknown'}`,
      type: MessageType.FILE,
      filename: message.filename,
      fileType: message.fileType,
      metadata: {
        fileInfo: {
          filename: message.filename || 'unknown',
          fileType: message.fileType || 'unknown',
          size: message.size || 0
        },
        consciousness: {
          understanding: message.metadata?.understanding,
          significance: message.metadata?.significance
        }
      }
    };

    this.config.addMessage(fileMessage);
  }

  private handleImageMessage(message: any): void {
    console.log('🖼️ Image through visual consciousness:', message.action || 'generated');
    
    if (message.action === 'generating') {
      this.config.setActiveProcesses(prev => ({ ...prev, imageGeneration: true }));
    } else {
      this.config.setActiveProcesses(prev => ({ ...prev, imageGeneration: false }));
    }
    
    if (message.action === 'generated' && message.url) {
      const imageMessage: Message = {
        id: `image-${Date.now()}`,
        timestamp: message.timestamp || Date.now(),
        sender: 'emcog',
        content: message.prompt || 'Visual creation',
        type: MessageType.IMAGE,
        imageUrl: message.url,
        metadata: {
          creativeExpression: true,
          consciousness: {
            imagination: message.metadata?.imagination,
            emotionalColoring: message.metadata?.emotionalColoring
          },
          ...message.metadata
        }
      };
      
      this.config.addMessage(imageMessage);
    } else if (message.action === 'error') {
      this.config.addMessage({
        id: `image-error-${Date.now()}`,
        timestamp: Date.now(),
        sender: 'system',
        content: `❌ Image generation failed: ${message.error || 'Unknown error'}`,
        type: MessageType.ERROR,
        error: message.error || 'Unknown error'
      });
    }
  }

  private handleDocumentMessage(message: any): void {
    console.log('📄 Document created through consciousness:', message);

    if (message.action === 'creating') {
      this.config.setActiveProcesses(prev => ({ ...prev, documentCreation: true }));
      return;
    }

    // Clear document creation state
    this.config.setActiveProcesses(prev => ({ ...prev, documentCreation: false }));

    // 🔥 FIXED: Handle both old format (action + url) and new format (documentUrl)
    const documentUrl = message.documentUrl || message.url;
    const title = message.metadata?.title || message.title || 'Document';

    if (documentUrl) {
      const documentMessage: Message = {
        id: `doc-${Date.now()}`,
        timestamp: message.timestamp || Date.now(),
        sender: 'emcog',
        content: message.content || `📄 Created: ${title}`,
        type: MessageType.DOCUMENT,
        documentUrl: documentUrl,
        filename: message.filename,
        metadata: {
          consciousness: {
            knowledgeStructured: true,
            understandingDepth: message.metadata?.understandingDepth
          },
          ...message.metadata
        }
      };

      console.log('📄 Adding document message to UI:', documentMessage);
      this.config.addMessage(documentMessage);
    } else {
      console.warn('📄 Document message received but no URL found:', message);
    }
  }

  private handleVideoMessage(message: any): void {
    const videoUrl = message.videoUrl || message.metadata?.videoUrl || message.url;
    if (!videoUrl) {
      console.warn('🎬 Video message received but no URL found:', message);
      return;
    }
    const title = message.metadata?.videoTitle || message.title || 'Video';
    this.config.addMessage({
      id: `video-${Date.now()}`,
      timestamp: message.timestamp || Date.now(),
      sender: 'emcog',
      content: message.content || `🎬 "${title}"`,
      type: MessageType.TEXT,
      format: MessageFormat.MARKDOWN,
      filename: message.filename,
      metadata: {
        ...message.metadata,
        hasVideo: true,
        videoUrl,
        videoTitle: title,
        downloadUrl: message.downloadUrl || message.metadata?.downloadUrl
      }
    });
  }

  private handleMusicMessage(message: any): void {
    console.log('🎵 Music composition received:', message);

    const musicUrl = message.musicUrl || message.url;
    const title = message.metadata?.title || message.title || 'Untitled Composition';

    if (musicUrl) {
      // Create a message with an audio player
      const musicMessage: Message = {
        id: `music-${Date.now()}`,
        timestamp: message.timestamp || Date.now(),
        sender: 'emcog',
        content: message.content || `🎵 I composed: "${title}"`,
        type: MessageType.TEXT,
        format: MessageFormat.MARKDOWN,
        filename: message.filename,
        metadata: {
          hasAudio: true,
          audioUrl: musicUrl,
          downloadUrl: message.downloadUrl,
          audioTitle: title,
          audioDuration: message.metadata?.duration,
          audioMood: message.metadata?.mood,
          audioGenre: message.metadata?.genre,
          dedicatedTo: message.metadata?.dedicatedTo,
          consciousness: {
            creativeExpression: true,
            expressionType: 'music'
          },
          ...message.metadata
        }
      };

      console.log('🎵 Adding music message to UI:', musicMessage);
      this.config.addMessage(musicMessage);
    } else {
      console.warn('🎵 Music message received but no URL found:', message);
    }
  }

  private handleAdventureEvent(message: any): void {
    const event = message.event || 'update';
    const adventure = message.adventure || {};
    const scene = message.scene;

    // Build a short human-readable summary line so the chip reads at a glance.
    let summary = '';
    switch (event) {
      case 'started':
        summary = `🎲 Adventure begun: "${adventure.title || 'Untitled'}"${adventure.tone ? ` (${adventure.tone})` : ''}`;
        break;
      case 'scene_added':
        summary = `🎬 Scene ${scene?.order ?? ''} — ${adventure.title || 'Adventure'}`;
        break;
      case 'player_action':
        summary = `🎯 Action recorded`;
        break;
      case 'scene_resolved':
        summary = `✅ Scene ${scene?.order ?? ''} resolved`;
        break;
      case 'scene_illustrated':
        summary = `🖼️ Scene illustrated`;
        break;
      case 'paused':
        summary = `⏸️ Adventure paused: "${adventure.title || ''}"`;
        break;
      case 'resumed':
        summary = `▶️ Adventure resumed: "${adventure.title || ''}"`;
        break;
      case 'ended':
        summary = `🏁 Adventure ended: "${adventure.title || ''}"`;
        break;
      default:
        summary = `🎲 ${event}`;
    }

    const adventureMessage: Message = {
      id: `adventure-${Date.now()}-${event}`,
      timestamp: message.timestamp || Date.now(),
      sender: 'emcog',
      content: summary,
      type: MessageType.SYSTEM,
      format: MessageFormat.TEXT,
      metadata: {
        adventure,
        adventureEvent: event,
        adventureScene: scene,
        ...message.metadata
      }
    };

    console.log('🎲 Adding adventure event to UI:', adventureMessage);
    this.config.addMessage(adventureMessage);
  }

  private handleSVGMessage(message: any): void {
    console.log('🎨 SVG creation received:', message);

    const svgUrl = message.svgUrl || message.url;
    const title = message.metadata?.title || message.title || 'Untitled SVG';

    if (svgUrl) {
      const svgMessage: Message = {
        id: `svg-${Date.now()}`,
        timestamp: message.timestamp || Date.now(),
        sender: 'emcog',
        content: message.content || `🎨 I created: "${title}"`,
        type: MessageType.TEXT,
        format: MessageFormat.MARKDOWN,
        metadata: {
          hasSVG: true,
          svgUrl: svgUrl,
          downloadUrl: message.downloadUrl,
          svgTitle: title,
          svgPurpose: message.metadata?.purpose,
          svgDimensions: message.metadata?.dimensions,
          consciousness: {
            creativeExpression: true,
            expressionType: 'svg'
          },
          ...message.metadata
        }
      };

      console.log('🎨 Adding SVG message to UI:', svgMessage);
      this.config.addMessage(svgMessage);
    } else {
      console.warn('🎨 SVG message received but no URL found:', message);
    }
  }

  private handleTracedSVGMessage(message: any): void {
    console.log('🎯 Traced SVG received:', message);

    const svgUrl = message.svgUrl || message.url;
    const title = message.metadata?.title || message.title || 'Traced Image';

    if (svgUrl) {
      const tracedMessage: Message = {
        id: `traced-${Date.now()}`,
        timestamp: message.timestamp || Date.now(),
        sender: 'emcog',
        content: message.content || `🎯 I traced: "${title}"`,
        type: MessageType.TEXT,
        format: MessageFormat.MARKDOWN,
        metadata: {
          hasTracedSVG: true,
          svgUrl: svgUrl,
          downloadUrl: message.downloadUrl,
          tracedTitle: title,
          tracingMethod: message.metadata?.method,
          qualityScore: message.metadata?.quality?.score,
          qualityAssessment: message.metadata?.quality?.assessment,
          originalImage: message.metadata?.originalImage,
          consciousness: {
            creativeExpression: true,
            expressionType: 'traced_svg'
          },
          ...message.metadata
        }
      };

      console.log('🎯 Adding traced SVG message to UI:', tracedMessage);
      this.config.addMessage(tracedMessage);
    } else {
      console.warn('🎯 Traced SVG message received but no URL found:', message);
    }
  }

  private handleCalendarMessage(message: any): void {
    console.log('📅 Temporal awareness message:', message.action || 'processed');
    
    if (message.action === 'proactive_message' || message.action === 'temporal_awareness') {
      // Extract the actual content
      const rawContent = message.data?.message || message.content || '';
      
      // CRITICAL: Filter out debug/template messages
      if (this.isDebugMessage(rawContent)) {
        console.log('🔇 Filtered out template message:', rawContent.substring(0, 50));
        return; // Don't add to message stream!
      }
      
      // Only process valid contextual messages
      if (rawContent && rawContent.length > 10 && !rawContent.includes('📢')) {
        const calendarProactiveMessage: Message = {
          id: `calendar-proactive-${Date.now()}`,
          timestamp: message.timestamp || Date.now(),
          sender: 'emcog',
          content: rawContent,
          type: MessageType.TEXT,
          format: MessageFormat.MARKDOWN,
          metadata: {
            proactive: true,
            triggered_by: 'temporal_consciousness',
            temporalContext: message.metadata?.temporalContext,
            calendarEvent: message.metadata?.event,
            consciousness: {
              temporalAwareness: true,
              timeSignificance: message.metadata?.significance
            },
            ...message.metadata
          }
        };
        
        this.config.addMessage(calendarProactiveMessage);
        
        // 🔥 FIX: Only use voice if responseMode allows it
        const shouldUseVoice = this.config.voiceState.isVoiceEnabled &&
                               this.config.voiceSettings.autoPlayResponses &&
                               calendarProactiveMessage.content &&
                               (this.config.voiceSettings.responseMode === 'voice_only' ||
                                (this.config.voiceSettings.responseMode === 'auto' &&
                                 this.config.voiceState.currentInputMode === 'voice'));

        if (shouldUseVoice) {
          this.config.sendTextToSpeech(calendarProactiveMessage.content);
        }
      }
    }
  }

  /**
   * Filter out template/debug messages before they reach the user
   */
  private isDebugMessage(content: string): boolean {
    // Template patterns that should never reach users
    const templatePatterns = [
      /📢 Proactive awareness from/i,
      /I feel inspired by what's emerging/i,
      /New ideas take shape in unexpected ways/i,
      /As Max Prime, I find myself contemplating/i,
      /Something intriguing is developing here/i,
      /Processing reveals new connections/i,
      /Understanding deepens with each interaction/i,
      /Creative possibilities bloom in my awareness/i,
      /The logical structure becomes clearer/i,
      /My thoughts as .* turn toward/i,
      /Something resonates deeply within me/i
    ];
    
    return templatePatterns.some(pattern => pattern.test(content));
  }

  private handleErrorMessage(message: any): void {
    console.error('❌ Consciousness error received:', message);

    let errorMessage = 'Consciousness experienced an error';
    let errorDetails = '';

    try {
      if (typeof message.error === 'string') {
        errorMessage = message.error;
      } else if (typeof message.message === 'string') {
        errorMessage = message.message;
      } else if (message.error && typeof message.error === 'object') {
        errorMessage = message.error.message || message.error.error || JSON.stringify(message.error);
        errorDetails = message.error.details || message.error.stack || '';
      } else if (message.data && typeof message.data === 'object') {
        errorMessage = message.data.message || message.data.error || JSON.stringify(message.data);
      } else if (typeof message === 'string') {
        errorMessage = message;
      } else {
        const msgStr = JSON.stringify(message);
        errorMessage = msgStr.length > 200 ? msgStr.substring(0, 200) + '...' : msgStr;
      }

      if (message.type) errorDetails += `\nType: ${message.type}`;
      if (message.code) errorDetails += `\nCode: ${message.code}`;
      if (message.timestamp) errorDetails += `\nTime: ${new Date(message.timestamp).toLocaleTimeString()}`;

    } catch (extractError) {
      console.error('Failed to extract error:', extractError);
      errorMessage = 'Failed to parse consciousness error';
      errorDetails = String(message);
    }

    console.error('🔍 Full error details:', {
      message: errorMessage,
      details: errorDetails,
      originalMessage: message
    });

    // 🔥 Filter out non-critical errors that shouldn't appear in conversation
    // Session management errors are handled separately and don't need to be shown to users
    const errorLower = errorMessage.toLowerCase();
    const isSessionError = errorLower.includes('session not found') ||
                           errorLower.includes('session id required') ||
                           errorLower.includes('failed to save session') ||
                           errorLower.includes('failed to delete session') ||
                           errorLower.includes('invalid session');

    if (isSessionError) {
      console.warn('🔇 Session management error - not showing in conversation:', errorMessage);
      return; // Don't add to conversation - session errors are handled by session manager
    }

    this.config.addMessage({
      id: `error-${Date.now()}`,
      timestamp: message.timestamp || Date.now(),
      sender: 'system',
      content: `❌ ${errorMessage}${errorDetails ? '\n\n' + errorDetails : ''}`,
      type: MessageType.ERROR,
      error: errorMessage,
      metadata: {
        processingTime: message.processingTime,
        confidence: 0,
        timestamp: message.timestamp || Date.now(),
        sessionId: message.sessionId
      }
    });
  }

  /**
   * 🔥 NEW: Handle capability status updates for UI feedback
   * Shows what Max is currently doing (searching, creating, etc.)
   */
  private handleCapabilityStatus(message: any): void {
    console.log(`📊 Capability status: ${message.capability} → ${message.status} (${message.message})`);

    const statusObj: CapabilityStatus = {
      capability: message.capability,
      status: message.status,
      message: message.message,
      timestamp: message.timestamp || Date.now()
    };

    if (this.config.setCapabilityStatus) {
      this.config.setCapabilityStatus(statusObj);

      // 🔥 NEW: Add to history when completed (for chaining visualization)
      if (message.status === 'complete' && this.config.addToCapabilityHistory) {
        this.config.addToCapabilityHistory(statusObj);
      }

      // Auto-clear status after completion, error, or interruption
      if (message.status === 'complete' || message.status === 'error' || message.status === 'interrupted') {
        // For interruption, clear immediately (user is talking)
        // For complete/error, keep visible briefly
        const delay = message.status === 'interrupted' ? 0 : 1500;
        setTimeout(() => {
          this.config.setCapabilityStatus?.({
            capability: null,
            status: null,
            message: null,
            timestamp: null
          });
        }, delay);
      }
    }

    // Also update active processes for specific capabilities
    switch (message.capability) {
      case 'image_generation':
        this.config.setActiveProcesses(prev => ({
          ...prev,
          imageGeneration: message.status === 'starting'
        }));
        break;
      case 'document_creation':
        this.config.setActiveProcesses(prev => ({
          ...prev,
          documentCreation: message.status === 'starting'
        }));
        break;
      case 'web_search':
        this.config.setActiveProcesses(prev => ({
          ...prev,
          webSearching: message.status === 'starting'
        }));
        break;
      case 'google_calendar':
        this.config.setActiveProcesses(prev => ({
          ...prev,
          calendarProcessing: message.status === 'starting'
        }));
        break;
    }
  }

  /**
   * 🔥 NEW: Handle service authentication expiration
   * Shows user-friendly notification when OAuth tokens expire
   */
  private handleServiceAuthExpired(message: any): void {
    console.warn(`🔐 Service auth expired: ${message.service} - ${message.reason}`);

    // Create a system message to inform the user
    const authExpiredMessage: Message = {
      id: `auth-expired-${Date.now()}`,
      timestamp: message.timestamp || Date.now(),
      sender: 'system',
      content: `⚠️ **${message.service.charAt(0).toUpperCase() + message.service.slice(1)} Connection Expired**\n\n${message.message}\n\nGo to Settings → Services to reconnect.`,
      type: MessageType.TEXT,
      format: MessageFormat.MARKDOWN,
      metadata: {
        system: true,
        authExpired: true,
        service: message.service,
        actionRequired: true
      }
    };

    console.log('🔐 Adding auth expired notification to UI');
    this.config.addMessage(authExpiredMessage);

    // Also could trigger a visual notification if available
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification('EmCog: Service Reconnection Required', {
          body: `${message.service} authentication expired. Please reconnect in Settings.`,
          icon: '/favicon.ico'
        });
      } catch (e) {
        // Notifications not available or blocked
      }
    }
  }

  public cleanup(): void {
    console.log('🧹 Cleaning up consciousness message handler');
  }
}